<template>  
  <v-container v-if="!isLoading">
    <v-col cols="12">
      <v-alert type="error" v-if="errorMessage">{{ errorMessage }}</v-alert>
    </v-col>
    <v-col cols="12" class="d-flex justify-space-between align-center">
      <h1>{{ $t("entry.title") }} {{ event.event_name }}</h1>
      <v-btn color="primary" text :to="'/soutez/' + event.id" style="height:50px; width:40px;" :title="$t('entry.back_to_event_title')"><v-icon size="28">mdi-arrow-left-circle</v-icon></v-btn>
    </v-col>
    
    <v-list density="compact">
      <v-list-item>
        {{ $t("entry.date_range") }} {{ $formatDate(event.event_date_start) }} - {{ $formatDate(event.event_date_end) }}
      </v-list-item>
      <v-list-item v-if="bulletinFileLocation">
        <a :href="bulletinFileLocation" target="_blank">{{ $t("entry.bulletin") }}</a>
      </v-list-item>
    </v-list>
    <v-alert v-if="race0.message_entry" type="info">{{ race0.message_entry }}</v-alert>

    <v-progress-circular v-if="isLoadingEdit && userStore.isLoggedIn" indeterminate color="primary" style="display:block; margin-bottom: 10px;"></v-progress-circular>
    <v-card v-if="Object.keys(allEntries).length">
      <v-card-title v-if="!entryClose">{{ $t("entry.edit_title") }}</v-card-title>

    <v-card-text>

      <v-row>

        <v-col cols="12" md="3">
        <!-- Rozevírací seznam pro výběr Entry -->
        <v-select
          :items="entryOptions"
          item-title="label"
          item-value="value"
          v-model="selectedEntryId"
          :label="$t('entry.select_entry_label')"
          @change="onEntrySelect">
        </v-select>
      </v-col>

      <v-col cols="12" md="2" v-if="mainEntry && !event.disable_si_cards">
        <v-text-field 
          v-model="mainEntry.si_number" 
          :label="$t('entry.si_number_label')"
          type="number"
          :rules="[rules.siNumber]"
          outlined
          dense
        ></v-text-field>
      </v-col>

      <v-col cols="12" md="2" v-if="mainRace">   
      <!-- Výběr kategorie pro Main Entry -->
      <v-tooltip location="top center" origin="auto" :text="$t('entry.main_category_help')">
        <template v-slot:activator="{ props }">
          <v-select
            v-model="selectedCategories[mainRace.id]"
            :items="filteredMainRaceCategories"
            item-title="category_name"
            item-value="id"
            :label="$t('entry.main_category_label')"
            v-bind="props"
            :hide-details="!mainRaceFeeEvent"
            >
              <template #details>
                <div v-if="mainRaceFeeEvent" class="text-green-darken-2 ">
                  {{ mainRaceFeeEvent }} {{ currencySymbol(event.event_currency) }}
                </div>
              </template>
          </v-select>
        </template>
      </v-tooltip>  
    </v-col>

    <v-col cols="12" md="3" v-if="mainEntry">
      <v-text-field 
        v-model="mainEntry.note" 
        :label="$t('entry.note_label')"
        maxlength="80"
        counter 
      ></v-text-field>
    </v-col>

    <v-col cols="12" md="2" v-if="(mainEntry && !event.disable_si_cards && siRentData && (siRentData.price !== null || siRentChecked))">
      <v-checkbox
        v-model="siRentChecked"
        :label="$t('entry.si_rent_label')"
      ></v-checkbox>
    </v-col>
  </v-row>

    <!-- Sekce pro Other Entries (ostatní závody) -->
    <v-row justify="center">
      <v-col v-for="race in filteredRaces" :key="race.id" cols="12" md="3" class="bg-entry-surface">
        <v-card class="ma-2">
          <div class="d-flex justify-space-between align-center">
            <v-card-title class="pb-0">{{ race.race_name }}</v-card-title>
            <template v-if="selectedCategories[race.id]">
              <span class="text-green-darken-2 mr-3">
                {{
                  (() => {
                    const category = race.categories.find(cat => cat.id === selectedCategories[race.id]);
                    if (!category) return "0"; // Pokud kategorie neexistuje, vrátíme "0"
                    
                    const fee = mainEntry && mainEntry.termin_prihl === "2" && category.increased_fee 
                      ? category.increased_fee 
                      : category.fee;

                    return fee ?? "0"; // Pokud fee není definováno, vrátíme "0"
                  })()
                }} {{ currencySymbol(event.event_currency) }}
              </span>
            </template>
          </div>
          <div style="font-size:11px" class="pl-4">{{ $formatDate(race.race_date) }}</div>

          <v-card-text class="pb-1">
            <v-select
              v-model="selectedCategories[race.id]"
              :items="race.categories"
              item-title="category_name"
              item-value="id"
              :label="$t('entry.category_for_race_label')"
              density="comfortable"
              hide-details
              class="mb-3"
            >
            </v-select>
            <v-select v-if="event.allow_start_choice" density="comfortable" :items="START_CHOICE_ITEMS" v-model="selectedStarts[race.id]" :label="$t('entry.start_choice_label')" hide-details class="pb-1"></v-select>

          </v-card-text>
        </v-card>
      </v-col>
    </v-row>



    <v-col v-if="!isLoading && Object.keys(accommodationsByDate).length">
            <h2 class="py-3">{{ $t('entry.accommodation_title') }}</h2>

            <!-- Nový oddíl pro zobrazení volných míst u všech variant ubytování s limitem -->
            <v-row>
              <v-col v-for="(item, index) in uniqueAccommodations" :key="index">
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle :class="{ 'text-red': item.freeSpaces === 0 }">
                  {{ $t('entry.accommodation_free') }} {{ item.freeSpaces }} / {{ item.limit }}
                </v-list-item-subtitle>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="3" v-for="date in sortedAccommodationDates" :key="'acc-' + date">
                <template v-if="accommodationsByDate[date]">
                <div class="d-flex justify-space-between">
                  <h3>{{ dayRangeLabel(date) }}</h3>
                  <template v-if="selectedAccommodations[date]">
                    <span v-if="accommodationsByDate[date].length > 1" class="text-green-darken-2">
                      {{ isOrgSelectedInRace0Edit ? 0 : accommodationsByDate[date].find(acc => acc.id === selectedAccommodations[date])?.price }} {{ currencySymbol(event.event_currency) }}
                    </span>
                    <span v-else class="text-green-darken-2">
                      {{ isOrgSelectedInRace0Edit ? 0 : accommodationsByDate[date][0].price }} {{ currencySymbol(event.event_currency) }}
                    </span>
                  </template>
                </div>
                <template v-if="accommodationsByDate[date].length === 1">
                  <v-checkbox
                    v-model="selectedAccommodations[date]"
                    :label="accommodationsByDate[date][0].name"
                    :true-value="accommodationsByDate[date][0].id"
                    :false-value="''"
                    @click="onSelectClick(date)"
                    hide-details
                    density="comfortable"
                    :disabled="shouldDisableEdit"
                  ></v-checkbox>
                </template>
                <template v-else>
                  <v-select
                    :items="accommodationsByDate[date]"
                    item-title="name"
                    item-value="id"
                    v-model="selectedAccommodations[date]"
                    :label="$t('entry.accommodation_select_label')"
                    clearable
                    @click="onSelectClick(date)"
                    hide-details
                    density="comfortable"
                    :disabled="shouldDisableEdit"
                  ></v-select>
                </template>
                </template>
              </v-col>
            </v-row>
          </v-col>

          <!-- Sekce pro jídlo -->
          <v-col v-if="Object.keys(mealsByDate).length">
            <h2 class="py-3">{{ $t('entry.meals_title') }}</h2>
            <v-row>
              <v-col cols="12" md="3" v-for="(meals, date) in mealsByDate" :key="'meal-' + date" class="border-t-md border-b-md">
                <h3 class="mb-3">{{ $formatDate(date) }}</h3>
                <div v-for="mealCode in FOOD_ORDER" :key="mealCode">
                  <div v-if="meals.filter(meal => meal.type === mealCode && meal.price !== '').length">
                    {{ foodLabel(mealCode) }}
                    <span v-if="selectedMeals[date][mealCode]" class="text-green-darken-2">
                      {{ isOrgSelectedInRace0Edit ? 0 : meals.find(meal => meal.id === selectedMeals[date][mealCode]).price }} {{ currencySymbol(event.event_currency) }}
                    </span>
                  </div>
                  <template v-if="meals.filter(meal => meal.type === mealCode).length === 1">
                    <v-checkbox
                      v-model="selectedMeals[date][mealCode]"
                      :label="meals.find(meal => meal.type === mealCode).name"
                      :true-value="meals.find(meal => meal.type === mealCode).id"
                      :false-value="null"
                      hide-details
                      density="comfortable"
                      :disabled="shouldDisableEdit"
                    />
                  </template>
                  <template v-else>
                    <v-select
                      v-if="meals.some(meal => meal.type === mealCode)"
                      :items="meals.filter(meal => meal.type === mealCode)"
                      item-title="name"
                      item-value="id"
                      v-model="selectedMeals[date][mealCode]"
                      :label="`${$t('entry.meal_select_label')} ${foodLabel(mealCode)}`"
                      density="comfortable"
                      clearable
                      hide-details
                      class="mb-2"
                      :disabled="shouldDisableEdit"
                    />
                  </template>
                </div>
              </v-col>
            </v-row>
          </v-col>

          <v-col v-if="Object.keys(groupedOtherServices).length">
            <h2>{{ $t('entry.other_services_title') }}</h2>
            <v-row v-if="Object.keys(groupedOtherServices).length">
              <v-col
                v-for="(variants, groupKey, i) in groupedOtherServices"
                :key="groupKey"
                cols="12" md="4"
              >
                <!-- hlavička s cenou -->
                <div class="d-flex justify-space-between align-center mb-2">
                  <h3 class="ma-0">{{ $t('entry.service_title') }} #{{ i+1 }}</h3>
                  <!-- count/limit -->
                  <span
                    v-if="other_edit_limits[groupKey]"
                    class="text-caption"
                    :class="{ 'text-red': other_edit_limits[groupKey].remaining === 0 }"
                  >
                    {{ $t('entry.remaining') }} {{ other_edit_limits[groupKey].remaining }} / {{ other_edit_limits[groupKey].limit }}
                  </span>
                  <!-- cena -->
                  <span v-if="selectedOther[groupKey]" class="text-green-darken-2">
                    {{ variants.find(v => v.id === selectedOther[groupKey]).price }} {{ currencySymbol(event.event_currency) }}
                  </span>
                  <span v-else>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>

                </div>

                <template v-if="variants.length > 1">
                  <v-select
                    :items="variants"
                    item-title="name"
                    item-value="id"
                    v-model="selectedOther[groupKey]"
                    :label="$t('entry.variant_select_label')"
                    density="comfortable"
                    clearable
                    hide-details="true"
                    :disabled="shouldDisableEdit"
                  />
                </template>
                <template v-else>
                  <v-checkbox
                    v-model="selectedOther[groupKey]"
                    :true-value="variants[0].id"
                    :false-value="null"
                    :label="variants[0].name"
                    density="comfortable"
                    hide-details="true"
                    :disabled="shouldDisableEdit"
                  />
                </template>

                <div class="text-body-2">
                  {{ (variants.find(v => v.id === selectedOther[groupKey]) || variants[0]).description }}
                </div>
              </v-col>
            </v-row>
          </v-col>


          <v-row class="d-flex justify-center mt-3" no-gutters>
            <v-col v-if="!entryClose && !hideEditButtons">
              <div class="responsive-container">
                <v-btn @click="submitEntry" color="primary" :disabled="!isEditFormValid" :loading="isFormLoading" class="responsive-btn">{{ $t('entry.update_btn') }}</v-btn>
                <span class="price">{{ $t('entry.price_label') }}: {{ totalSumEdit }} {{ currencySymbol(event.event_currency) }}</span>
                <v-btn color="error" @click="openDeleteDialog" class="responsive-btn">{{ $t('entry.delete_btn') }}</v-btn>
              </div>
            </v-col>
          </v-row>
            <v-alert variant="tonal" type="info" v-if="hideEditButtons" class="mt-2">{{ $t('entry.cant_edit_alert') }}</v-alert> 



          <!-- Dialog pro potvrzení smazání přihlášky -->
          <v-dialog v-model="deleteDialog" persistent max-width="600px">
            <v-card>
              <v-card-title class="text-h5">{{ $t('entry.delete_dialog_title') }}</v-card-title>
              <v-card-text>
                <i18n-t keypath="entry.delete_dialog_text">
                  <strong>{{ currentEntry.mainEntry.first_name }} {{ currentEntry.mainEntry.last_name }}</strong>
                </i18n-t>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="deleteDialog = false">{{ $t('entry.cancel') }}</v-btn>
                <v-btn color="red darken-1" text @click="deleteEntries">{{ $t('entry.confirm_delete') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card-text>
        </v-card>


  


  <!-- ******************************************
  ****                                  ******
  ****  Část pro vytvoření přihlášky    ******  
  ****                                  ******  
  ********************************************* -->

  <v-progress-circular v-if="isLoadingCreate && userStore.isLoggedIn" indeterminate color="primary"></v-progress-circular>
  <v-alert
    :title="$t('entry.one_time_title')"
    :text="$t('entry.one_time_text')"
    type="warning"
    variant="tonal"
    v-if="!userStore.isLoggedIn"
  />
  <v-card v-if="!isLoadingCreate && Object.keys(users).length && !entryClose || !userStore.isLoggedIn && !entryClose && !event.auth_entry_only" class="bg-entry-surface mt-2">
    <v-row>
      <v-col cols="12" md="3" class="pb-0">
        <v-card-title>{{ $t('entry.create_title') }}</v-card-title>
      </v-col>
      <v-col cols="12" md="3" v-if="users.length > 1">
        <v-btn color="green" @click="openBulkDialog" class="responsive-btn mt-2">{{ $t('entry.bulk_title') }}</v-btn>
      </v-col>
    </v-row>



    <EntryPageBulkDialog
      v-if="dialogBulkEntry"
      :users="users"
      :event="event"
      :races="races"
      :race0="race0"
      :accommodations-by-date="accommodationsByDate"
      :meals-by-date="mealsByDate"
      :grouped-other-services="groupedOtherServices"
      :si-rent-data="siRentData"
      :should-disable-create="shouldDisableCreate"
      :event-id="eventId"
      @close="dialogBulkEntry = false"
      @success="onBulkEntrySuccess"
    />


    <v-card-text>
    
      <v-form @submit.prevent="submitEntries" v-if="!isLoading">
        <template v-if="userStore.isLoggedIn"> <!-- Pokud je uživatel přihlášený, zobrazí se tento formulář -->
          <v-row>
          <v-col cols="12" md="3">
            <!-- Rozevírací seznam pro výběr uživatele -->
            <v-select
              :items="userOptions"
              item-text="label"
              item-value="value"
              v-model="selectedUserId"
              :label="$t('entry.select_user_label')">
            </v-select>
          </v-col>

          <v-col cols="12" md="2" v-if="!event.disable_si_cards">
            <v-text-field v-model="selectedUser.si_number" type="number" :label="$t('entry.si_number_label')" :rules="[rules.siNumber]"></v-text-field>
          </v-col>

          <v-col cols="12" md="2">
            <v-tooltip location="top center" origin="auto" :text="$t('entry.main_category_help')">
              <template v-slot:activator="{ props }">
              <v-select
                :items="race0.categories"
                item-title="category_name"
                item-value="id"
                v-model="selectedCategoryRace0"
                :label="$t('entry.main_category_label')"
                :rules="categoryMain"
                no-data-text="entry.no_categories"
                v-bind="props"
                :hide-details="!mainRaceFeeEventCreate"
                >
                  <template #details>
                    <div v-if="mainRaceFeeEventCreate" class="price-hint text-success">
                      {{ mainRaceFeeEventCreate }} {{ currencySymbol(event.event_currency) }}
                    </div>
                  </template>
              </v-select>
            </template>
          </v-tooltip>  

          </v-col>

          <v-col cols="12" md="3">
            <v-text-field v-model="selectedUser.note" maxlength="80" counter :label="$t('entry.note_for_org_label')"></v-text-field>
          </v-col>

          <v-col cols="12" md="2" v-if="(!event.disable_si_cards && siRentData && (siRentData.price !== null))">
            <v-checkbox v-model="selectedUser.si_rent" :label="$t('entry.si_rent_label')"></v-checkbox>
          </v-col>
        </v-row>

          <v-row justify="center">
            <v-col v-for="race in races" :key="race.id" cols="12" md="3" class="bg-entry-surface"> <!-- Nastavte šířku sloupce podle počtu závodů, které chcete mít vedle sebe -->
              <v-card class="ma-2">


                <div class="d-flex justify-space-between align-center">
                  <v-card-title class="pb-0">{{ race.race_name }}</v-card-title>
                  <template v-if="selectedCategoriesForCreateEntry[race.id]">
                   
                    <span class="text-green-darken-2 mr-3">
                      {{
                        (() => {
                          const category = race.categories.find(cat => cat.id === selectedCategoriesForCreateEntry[race.id]);
                          if (!category) return "0"; // Pokud kategorie neexistuje, vrátíme "0"

                          // Pokud jsme před entry_deadline_1, použijeme fee (nebo 0)
                          if (new Date() < new Date(race0.entry_deadline_1)) {
                            return category.fee ?? "0";
                          } 
                          
                          // Pokud jsme mezi entry_deadline_1 a entry_deadline_2
                          if (race0.entry_deadline_2 && new Date(race0.entry_deadline_1) < new Date() && new Date() < new Date(race0.entry_deadline_2)) {
                            return category.increased_fee ?? category.fee ?? "0"; // Použij increased_fee, pokud není, použij fee, jinak 0
                          } 
                          
                          // Jinak (po deadline_2 nebo pokud není deadline_2) použijeme běžné fee nebo 0
                          return category.fee ?? "0";
                        })()
                      }} {{ currencySymbol(event.event_currency) }}
                    </span>
                  </template>
                </div>
                <div style="font-size:11px" class="pl-4">{{ $formatDate(race.race_date) }}</div>

                <v-card-text class="pb-1">
                  <v-select
                    :items="race.categories"
                    item-title="category_name"
                    item-value="id"
                    v-model="selectedCategoriesForCreateEntry[race.id]"
                    :label="$t('entry.category_for_race_label')"
                    :key="race.id"
                    clearable
                    density="comfortable"
                  ></v-select>
                  <v-select v-if="event.allow_start_choice" density="comfortable" class="pb-1" hide-details :items="START_CHOICE_ITEMS" v-model="selectedStartsEntry[race.id]" :label="$t('entry.start_choice_label')"></v-select>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
  
        </template>

        <template v-else>    <!-- Pokud uživatel není v robisu přihlášený, zobrazí se mu tento formulář -->
          <div class="ml-1 mb-4">{{ $t('rules.required_label') }}</div>
          <v-row> 
            <v-col cols="12" md="2">
            <v-text-field v-model="selectedUser.first_name" :label="$t('entry.first_name_required')" :rules= "requiredField"></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field v-model="selectedUser.last_name" :label="$t('entry.last_name_required')" :rules= "requiredField"></v-text-field>
            </v-col>

            <!-- <v-col cols="12" md="2">
              <v-text-field v-model="selectedUser.index" label="Index"></v-text-field>
            </v-col> -->


            <v-col cols="12" md="2" v-if="!event.disable_si_cards">
              <v-text-field v-model="selectedUser.si_number" type="number" :label="$t('general.si_number_label')" :rules="[rules.siNumber]"></v-text-field>
            </v-col>

            <v-col cols="12" md="2">
              <v-text-field v-model="selectedUser.birth_year" :label="$t('entry.birth_year_required')" :rules="[rules.bornYear]"></v-text-field>
            </v-col>

            <v-col cols="12" md="2">
              <v-select v-model="selectedUser.competitor_gender" :items="genders" :label="$t('entry.gender_required')"></v-select>
            </v-col>

            <v-col cols="12" md="2">
              <v-select
                :items="race0.categories"
                item-title="category_name"
                item-value="id"
                v-model="selectedCategoryRace0"
                :label="$t('entry.main_category_label')"
                :no-data-text="$t('entry.no_categories')"
                :rules="categoryMain">
              </v-select>
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field v-model="selectedUser.email" label="Email" :rules="validEmail"></v-text-field>
            </v-col>

            <v-col cols="12" md="2">
              <v-select
                v-model="selectedUser.competitor_club_shortcut"
                :items="clubShortcuts"
                label="Oddíl"
                clearable
              />
            </v-col>


            <v-col cols="12" md="3">
              <v-text-field v-model="selectedUser.note" maxlength="80" counter :label="$t('entry.note_for_org_label')"></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <CountrySelect v-model="selectedUser.country" :label="$t('entry.country_required')" />
            </v-col>
            <v-col cols="12" md="2" v-if="(!event.disable_si_cards && siRentData && (siRentData.price !== null))">
              <v-checkbox v-model="selectedUser.si_rent" :label="$t('entry.si_rent_label')" />
            </v-col>
          </v-row>
          
          <v-row justify="center">
            <v-col v-for="race in races" :key="race.id" cols="12" md="3" class="bg-entry-surface"> 
              <v-card class="ma-2">
              <div class="d-flex justify-space-between align-center">
                <v-card-title>{{ race.race_name }}</v-card-title>
                <template v-if="selectedCategoriesForCreateEntry[race.id]">
                        
                  <span class="text-green-darken-2 mr-3">
                    {{
                      new Date() < new Date(race0.entry_deadline_1)
                        ? race.categories.find(cat => cat.id === selectedCategoriesForCreateEntry[race.id])?.fee
                        : race0.entry_deadline_2 && new Date(race0.entry_deadline_1) < new Date() && new Date() < new Date(race0.entry_deadline_2) && race.categories.find(cat => cat.id === selectedCategoriesForCreateEntry[race.id])?.increased_fee
                          ? race.categories.find(cat => cat.id === selectedCategoriesForCreateEntry[race.id])?.increased_fee
                          : race.categories.find(cat => cat.id === selectedCategoriesForCreateEntry[race.id])?.fee
                    }} {{ currencySymbol(event.event_currency) }}
                  </span>
                </template>
              </div>

                <v-card-text>

                  <v-select
                    :items="race.categories"
                    item-title="category_name"
                    item-value="id"
                    v-model="selectedCategoriesForCreateEntry[race.id]"
                    :label="$t('entry.category_for_race_label')"
                    :key="race.id"
                    clearable
                    density="comfortable"
                  ></v-select>
                  <v-select v-if="event.allow_start_choice" density="comfortable" hide-details="true" :items="START_CHOICE_ITEMS" v-model="selectedStartsEntry[race.id]" :label="$t('entry.start_choice_label')"></v-select>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

        </template>

        <v-col v-if="!isLoading && Object.keys(accommodationsByDate).length && !shouldDisableCreate" class="pt-0">
            <h2 class="py-3">{{ $t('entry.accommodation_title') }}</h2>

            <!-- Nový oddíl pro zobrazení volných míst u všech variant ubytování s limitem -->
            <v-row>
              <v-col v-for="(item, index) in uniqueAccommodations" :key="index">
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle :class="{ 'text-red': item.freeSpaces === 0 }">
                  {{ $t('entry.accommodation_free') }} {{ item.freeSpaces }} / {{ item.limit }}
                </v-list-item-subtitle>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="3" v-for="date in sortedAccommodationDates" :key="'acc-' + date">
                <template v-if="accommodationsByDate[date]">
                <div class="d-flex justify-space-between">
                  <h3>{{ dayRangeLabel(date) }}</h3>
                  <template v-if="createSelectedAccommodations[date]">
                    <span v-if="accommodationsByDate[date].length > 1" class="text-green-darken-2">
                      {{ isOrgSelectedInRace0Create ? 0 : accommodationsByDate[date].find(acc => acc.id === createSelectedAccommodations[date])?.price }} {{ currencySymbol(event.event_currency) }}
                    </span>
                    <span v-else class="text-green-darken-2">
                      {{ isOrgSelectedInRace0Create ? 0 : accommodationsByDate[date][0].price }} {{ currencySymbol(event.event_currency) }}
                    </span>
                  </template>
                </div>
                <template v-if="accommodationsByDate[date].length === 1">
                  <v-checkbox
                    v-model="createSelectedAccommodations[date]"
                    :label="accommodationsByDate[date][0].name"
                    :true-value="accommodationsByDate[date][0].id"
                    :false-value="''"
                    clearable
                    @click="onSelectClickCreate(date)"
                    density="comfortable"
                    hide-details="true"
                  ></v-checkbox>
                </template>
                <template v-else>
                  <v-select
                    :items="accommodationsByDate[date]"
                    item-title="name"
                    item-value="id"
                    v-model="createSelectedAccommodations[date]"
                    :label="$t('entry.accommodation_select_label')"
                    clearable
                    class="mb-1"
                    @click="onSelectClickCreate(date)"
                    density="comfortable"
                    hide-details="true"
                  ></v-select>
                </template>
                </template>
              </v-col>
            </v-row>
          </v-col>

          <v-col v-if="Object.keys(mealsByDate).length && !shouldDisableCreate" class="pt-0">
            <h2 class="py-3">{{ $t('entry.meals_title') }}</h2>
            <v-row>
              <v-col cols="12" md="3" v-for="(meals, date) in mealsByDate" :key="'meal-' + date" class="border-t-md border-b-md">
                <h3 class="mb-3">{{ $formatDate(date) }}</h3>
                <div v-for="mealCode in FOOD_ORDER" :key="mealCode">
                  <template v-if="meals.filter(meal => meal.type === mealCode).length === 1">
                    <div v-if="meals.filter(meal => meal.type === mealCode && meal.price !== '').length">
                      {{ foodLabel(mealCode) }}
                      <span v-if="createSelectedMeals[date][mealCode]" class="text-green-darken-2">
                        {{ isOrgSelectedInRace0Create ? 0 : meals.find(meal => meal.id === createSelectedMeals[date][mealCode]).price }} {{ currencySymbol(event.event_currency) }}
                      </span>
                    </div>
                    <v-checkbox
                      v-model="createSelectedMeals[date][mealCode]"
                      :label="meals.find(meal => meal.type === mealCode).name"
                      :true-value="meals.find(meal => meal.type === mealCode).id"
                      :false-value="null"
                      clearable
                      density="comfortable"
                      hide-details="true"
                    />
                  </template>
                  <template v-else>
                    <div v-if="meals.filter(meal => meal.type === mealCode && meal.price !== '').length">
                      {{ foodLabel(mealCode) }}
                      <span v-if="createSelectedMeals[date][mealCode]" class="text-green-darken-2">
                        {{ isOrgSelectedInRace0Create ? 0 : meals.find(meal => meal.id === createSelectedMeals[date][mealCode]).price }} {{ currencySymbol(event.event_currency) }}
                      </span>
                    </div>
                    <v-select
                      v-if="meals.some(meal => meal.type === mealCode)"
                      :items="meals.filter(meal => meal.type === mealCode)"
                      item-title="name"
                      item-value="id"
                      v-model="createSelectedMeals[date][mealCode]"
                      :label="`${$t('entry.meal_select_label')} ${foodLabel(mealCode)}`"
                      clearable
                      density="comfortable"
                      hide-details="true"
                      class="mb-1"
                    />
                  </template>
                </div>
              </v-col>
            </v-row>
          </v-col>



          <!-- Oddíl pro zobrazení Other služeb seskupených podle key -->
          <v-col v-if="otherServices.length && !shouldDisableCreate" class="pt-0">
            <h2 class="py-3">{{ $t('entry.other_services_title') }}</h2>
            <v-row class="d-flex flex-row flex-wrap">
              <v-col cols="12" md="3" v-for="(group, groupKey, groupIndex) in groupedOtherServices" :key="groupKey">
                <!-- Flex container pro nadpis a cenu vedle sebe -->
                <div class="d-flex justify-space-between align-center">
                  <h3 class="mb-0">
                    {{ $t('entry.service_title') }} #{{ groupIndex + 1 }} 
                  </h3>
                  <span 
                      class="text-caption"
                      v-if="other_create_limits[groupKey]" 
                      :class="{'text-red': other_create_limits[groupKey].remaining === 0}"
                    >
                      {{ $t('entry.remaining') }} {{ other_create_limits[groupKey].remaining }} / {{ other_create_limits[groupKey].limit }}
                  </span>
                  <span class="text-green-darken-2" v-if="createSelectedOther[groupKey]">
                    {{ group.length > 1 
                          ? group.find(item => item.id === createSelectedOther[groupKey])?.price 
                          : group[0].price 
                    }} {{ currencySymbol(event.event_currency) }}
                  </span>
                  <span v-else>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                </div>
                <!-- Formulářová část pro výběr varianty -->
                <template v-if="group.length > 1">
                  <v-select
                    :items="group"
                    item-title="name"
                    item-value="id"
                    v-model="createSelectedOther[groupKey]"
                    :label="$t('entry.variant_select_label')"
                    clearable
                    density="comfortable"
                    hide-details="true"
                    class="mb-1"
                  ></v-select>
                </template>
                <template v-else>
                  <v-checkbox
                    v-model="createSelectedOther[groupKey]"
                    :label="group[0].name"
                    :true-value="group[0].id"
                    :false-value="null"
                    density="comfortable"
                    hide-details="true"
                  ></v-checkbox>
                </template>
                <div>
                  <template v-if="createSelectedOther[groupKey]">
                    {{ group.find(item => item.id === createSelectedOther[groupKey])?.description }}
                  </template>
                  <template v-else>
                    {{ group[0].description }}
                  </template>
                </div>
              </v-col>
            </v-row>
          </v-col>

          <v-row class="mt-3" no-gutters>
            <v-btn type="submit" :loading="isFormLoading" class="responsive-btn" color="primary" :disabled="!isFormValid">{{ $t('entry.submit_btn') }}</v-btn>
            <div class="price mt-2">{{ $t('entry.price_label') }}: {{ totalSumCreate }} {{ currencySymbol(event.event_currency) }}</div>
          </v-row>
      </v-form>
      <v-progress-circular v-else indeterminate color="primary"></v-progress-circular>
    </v-card-text>
  </v-card>
  <v-alert type="error" v-if="event.auth_entry_only && !userStore.isLoggedIn">{{ $t('entry.auth_required') }}</v-alert>
  <v-alert variant="tonal" type="info" v-if="entryClose && !isLoading && !isLoadingCreate" class="mt-2">{{ entryClose }}</v-alert> 
  </v-container>
  <v-progress-circular v-else indeterminate color="primary"></v-progress-circular>

  <DialogConfirmLeave
    v-model="confirmLeaveDialog"
    :text="t('entry.unsaved_text')"
    @cancel="stayHere"          
    @confirm="confirmLeave"   
  />
  
  <!-- </template> -->
</template>


<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, reactive } from 'vue';
import apiClient from '/api.js';
import { useRoute, useRouter, onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { useAuthStore } from '/auth.js';
import CountrySelect from '@/components/shared/CountrySelect.vue';
import { genders } from '@/components/const/gender.js';
import {useSnackbarStore} from '@/stores/snackbarStore'
import {useI18n} from 'vue-i18n'
import { parseISO, addDays, format } from 'date-fns'
import { START_CHOICE_ITEMS, currencySymbol } from '@/components/const/enums.js'
import DialogConfirmLeave from '@/components/shared/DialogConfirmLeave.vue';
import EntryPageBulkDialog from '@/components/EntryPageBulkDialog.vue';
import {
  calculateUserAge,
  isUserInSameClub,
  filterCategoriesByAgeAndSex,
} from '@/utils/entryCategories.js';

const { t } = useI18n()

const isLoading = ref(true);  // Indikátor načítání
const isLoadingCreate = ref(true);  // Indikátor načítání pro vytvoření přihlášky
const isLoadingEdit = ref(true);  // Indikátor načítání pro editaci přihlášky

const snackbar = useSnackbarStore()
const route = useRoute();
const router = useRouter(); // Získání instance routeru

const userStore = useAuthStore();
const eventId = route.params.id;
const event = ref({ entry_is_open: false }); // údaje o eventu
const races = ref([]);
const allRaces = ref([]);
const race0 = ref({});
const users = ref([]);
const selectedUser = ref({ si_number: '', note: '', competitor_gender: '', birth_year: null, competitor_club: null, competitor_club_shortcut: null, country: 'CZE' });
const selectedUserId = ref(null);

// seznam oddílů pro jednorázové přihlášky (jen nepřihlášený)
const clubs = ref([]);
const clubShortcuts = computed(() =>
  (clubs.value || [])
    .map(c => (c?.club_shortcut || '').toString().trim())
    .filter(Boolean)
    .sort()
);


const entries = ref([]);
const selectedCategories = ref({});
const selectedCategoriesForCreateEntry = ref({});

const selectedCategoryRace0 = ref(null);

const mainEntry = ref(null); // Přidali jsme reaktivní referenci pro mainEntry
const mainRace = computed(() => allRaces.value[0]);
const selectedStarts = ref({});
const selectedStartsEntry = ref({});


const accommodationsByDate = ref({});
const mealsByDate = ref({});
const selectedAccommodations = ref({});
const selectedMeals = ref({});

// stable chronological order for ISO dates (YYYY-MM-DD)
const sortedAccommodationDates = computed(() =>
  Object.keys(accommodationsByDate.value || {}).sort()
)

const deleteDialog = ref(false);


// CREATE
const createSelectedAccommodations = ref({});
const createSelectedMeals          = ref({});
const createSelectedOther          = ref({});



function openDeleteDialog() {
  deleteDialog.value = true;
}

const allEntries = ref([]);
const selectedEntryId = ref(null);
const currentEntry = ref(null);

const errorMessage = ref('');

const rules = {
  siNumber: value => !value || /^\d{4,7}$/.test(value) || t('rules.si_range'),
  bornYear: value => { const currentYear = new Date().getFullYear();
    return value >= 1900 && value <= currentYear || t('rules.born_year_between', { currentYear });
  }
};

const weekdayKey = (d) => {
  const idx = Number(format(d, 'i')) // 1=PO ... 7=NE
  switch (idx) {
    case 1: return 'enums.weekdays.mon'
    case 2: return 'enums.weekdays.tue'
    case 3: return 'enums.weekdays.wed'
    case 4: return 'enums.weekdays.thu'
    case 5: return 'enums.weekdays.fri'
    case 6: return 'enums.weekdays.sat'
    case 7: return 'enums.weekdays.sun'
    default: return 'enums.weekdays.mon'
  }
}

function dayRangeLabel(dateStr) {
  if (!dateStr) return ''
  const d1 = parseISO(dateStr)
  const d2 = addDays(d1, 1)
  return `${t(weekdayKey(d1))}–${t(weekdayKey(d2))}`
}


// Výpočetní vlastnost pro kontrolu, zda jsou registrace uzavřené nebo termíny pro race0 vypršely nebo je aktivní 2. termín přihlášek
// pomocné
const toDate = v => v ? new Date(v) : null
const now = computed(() => new Date())

const deadline1 = computed(() => toDate(race0.value?.entry_deadline_1))
const deadline2 = computed(() => toDate(race0.value?.entry_deadline_2))

// 2. termín = první je už minulost a druhý je budoucnost
const isSecondTermActive = computed(() => {
  if (!deadline1.value || !deadline2.value) return false
  return now.value > deadline1.value && now.value < deadline2.value
})

// pokud budeš mít v eventu flag
const restrictServices = computed(() => !!event.value?.restrict_services)

// skrýt akční tlačítka při editaci, když přihláška vznikla v 1. termínu a už běží 2.
const hideEditButtons = computed(() =>
  isSecondTermActive.value && (mainEntry.value?.termin_prihl === "1")
)

// globální “disable” pro editační část
const shouldDisableEdit = computed(() => restrictServices.value && isSecondTermActive.value || hideEditButtons.value)
const shouldDisableCreate = computed(() => restrictServices.value && isSecondTermActive.value)

// entryClose – stejné datumy
const entryClose = computed(() => {
  if (isLoading.value) return ''
  if (!event.value.entry_is_open) return t('entry.closed_disabled')
  if (!deadline1.value && !deadline2.value) return ''          // nejsou nastavené
  if (!deadline2.value && deadline1.value && now.value > deadline1.value) return t('entry.closed_deadline')
  if (deadline2.value && now.value > deadline2.value) return t('entry.closed_deadline')
  return ''
})



// Vypočítaná vlastnost pro generování možností rozevíracího seznamu Entry
const entryOptions = computed(() => allEntries.value.map(entry => ({
  value: entry.user,
  label: `${entry.mainEntry.last_name} ${entry.mainEntry.first_name} ${entry.mainEntry.competitor_index ? `(${entry.mainEntry.competitor_index})` : ''} `
})));


const siRentChecked = ref(false); // Místo pro sledování checkboxu


function onEntrySelect(selectedUserId) {
  const selected = allEntries.value.find(entry => entry.user === selectedUserId);
  currentEntry.value = selected;
  mainEntry.value = selected.mainEntry;

  // Zkontrolujeme všechny závody a nastavíme výchozí hodnoty pro start_choice
  allRaces.value.forEach(race => {
    selectedStarts.value[race.id] = '';  // Nastavení defaultní hodnoty pro startovní skupiny
  });

  // Aktualizace selectedCategories na základě kategorií vybraného Entry
  selectedCategories.value = {};
  selected.otherEntries.forEach(oe => {
    selectedStarts.value[oe.race] = oe.start_preference;
    selectedCategories.value[oe.race] = oe.competitor_category;
  });

  // Nastavte vybranou kategorii pro hlavní závod
  if (selected.mainEntry && selected.mainEntry.race && selected.mainEntry.competitor_category) {
    selectedCategories.value[selected.mainEntry.race] = selected.mainEntry.competitor_category;
  }

  // Zkontrolujte, zda má uživatel registrovaný si_rent v race_services
  if (siRentData.value && selected.mainEntry.race_service.includes(siRentData.value.id)) {
    siRentChecked.value = true; // Uživatel má přihlášený si_rent
  } else {
    siRentChecked.value = false; // Uživatel nemá přihlášený si_rent
  }

  // Resetování předvybraných hodnot pro ubytování a jídlo
  Object.keys(accommodationsByDate.value).forEach(date => {
    selectedAccommodations.value[date] = null;
  });
  Object.keys(mealsByDate.value).forEach(date => {
    selectedMeals.value[date] = {};
    mealsByDate.value[date].forEach(meal => {
      selectedMeals.value[date][meal.type] = null;
    });
  });



  // Nastavení předvybraných ubytování a jídel podle dat uživatele
  selected.accommodations.forEach(accommodationId => {
    const dateKey = Object.keys(accommodationsByDate.value).find(date => 
      accommodationsByDate.value[date].some(acc => acc.id === accommodationId));
    if (dateKey) {
      selectedAccommodations.value[dateKey] = accommodationId;
    }
  });

  selected.food.forEach(foodId => {
    const dateKey = Object.keys(mealsByDate.value).find(date =>
      mealsByDate.value[date].some(meal => meal.id === foodId));
    if (dateKey) {
      const mealType = mealsByDate.value[dateKey].find(meal => meal.id === foodId).type;
      selectedMeals.value[dateKey][mealType] = foodId;
    }
  });

  // Inicializace other services
  Object.keys(groupedOtherServices.value).forEach(key => {
    selectedOther.value[key] = null;
  });

  // 2) předvyplnění z toho, co už má user v mainEntry.race_service
  (selected.mainEntry.race_service || []).forEach(id => {
    const svc = otherServices.value.find(x => x.id === id);
    if (!svc) return;
    const key = svc.key || `var_${svc.variant}`;
    selectedOther.value[key] = id;
  });
  snapshotBaseline()
}




// Funkce pro načtení Entries
async function fetchEntries() {
  isLoadingEdit.value = true;
  try {
    const response = await apiClient.get(`event/${eventId}/entry/edit/`);
    allEntries.value = response.data;
    // Nastavení prvního Entry jako výchozího vybraného nebo toho ,který už byl pvybraný předtím
    if (!selectedEntryId.value && allEntries.value.length > 0) {
      selectedEntryId.value = allEntries.value[0].user;
      currentEntry.value = allEntries.value[0];
      onEntrySelect(selectedEntryId.value);
    }
  } catch (error) {
    console.error('Failed to load entries:', error);
    errorMessage.value = t('entry.load_entries_failed');
  } finally {
    isLoadingEdit.value = false;
  }
}
  


// Computed property pro získání všech závodů kromě hlavního
const filteredRaces = computed(() => {
  return allRaces.value.slice(1).map(race => {
    const userAge = calculateUserAge(mainEntry.value?.birth_year);
    const userSex = mainEntry.value?.competitor_gender;
    const isSameClub = isUserInSameClub(mainEntry.value?.competitor_club, event.value.club);

    const filteredCategories = filterCategoriesByAgeAndSex(
      race.categories, 
      userAge, 
      userSex, 
      isSameClub
    );

    // Přidání prázdné možnosti do pole kategorií po filtraci
    const categoriesWithEmptyOption = [{ id: null, category_name: t('entry.not_registered'), option: "" }, ...filteredCategories];
    
    return {
      ...race,
      categories: categoriesWithEmptyOption,
    };
  });
});

// Vypočítaná vlastnost pro filtrované kategorie hlavního závodu
const filteredMainRaceCategories = computed(() => {
  // Získání hlavního závodu
  const mainRace = allRaces.value[0] || { categories: [] };
  const userAge = calculateUserAge(mainEntry.value?.birth_year);
  const userSex = mainEntry.value?.competitor_gender;
  const isSameClub = isUserInSameClub(mainEntry.value?.competitor_club, event.value.club);

  // Filtrace kategorií hlavního závodu
  return filterCategoriesByAgeAndSex(
    mainRace.categories,
    userAge,
    userSex,
    isSameClub
  );
});

// kategorie: calculateUserAge / isUserInSameClub / filterCategoriesByAgeAndSex z @/utils/entryCategories.js


// Výpočet částky za mainCategory při výběru kategorie
const mainRaceFeeEvent = computed(() => {
  const race = mainRace.value
  if (!race) return 0
  const selId = selectedCategories.value?.[race.id]
  if (!selId) return 0
  const fee = race.categories?.find(c => c.id === selId)?.fee_event ?? 0
  return Number(fee) || 0
})

const isOrgSelectedInRace0Edit = computed(() => {
  const race = mainRace.value
  if (!race) return false
  const selId = selectedCategories.value?.[race.id]
  if (!selId) return false
  const cat = race.categories?.find(c => c.id === selId)
  return cat?.option === 'ORG'
})


// sleduje změnu přihlášky z rozevíracího seznamu
watch(() => selectedEntryId.value, (newId) => {
  onEntrySelect(newId);

});


onMounted(async () => {
  try {
    await Promise.all([
      fetchEvent(eventId),
      fetchRaceServices(eventId),
      fetchEntries(),
      fetchUsers(eventId),
    ]);

    // Jednorázové přihlášky (nepřihlášený) potřebují seznam oddílů
    if (!userStore.isLoggedIn) {
      await fetchClubs()
    }
    
    // Zde nastavte vybrané ubytování a jídlo podle prvního uživatele
    if (allEntries.value.length > 0) {
      onEntrySelect(selectedEntryId.value);
      await nextTick()
      snapshotBaseline()
    }
  } catch (error) {
  }
});


// Kontrola vyplněných údajů pro přihlášeného uživatele
const isEditFormValid = computed(() => {
  if (mainEntry.value) {
    return rules.siNumber(mainEntry.value.si_number) === true;
  }
  return false;
});

// Po odeslání se znepřístupní tlačítko, dokud se data nevrátí z backendu
const isFormLoading = ref(false);

const selectedOther = ref({});
const otherServices = ref([]);


async function submitEntry() {
  const currentSelectedEntry = allEntries.value.find(entry => entry.user === selectedEntryId.value);
  const keepId = selectedEntryId.value; // Zachovej v select odesílanu entry

  if (!currentSelectedEntry) {
    console.error("Vybrané entry nebylo nalezeno.");
    return;
  }

  // Kontrola, zda je vyplněno si_number nebo zaškrtnutý si_rent
  // if (!mainEntry.value.si_number && !siRentChecked.value) {
  //   errorMessage.value = 'Musíš buď vyplnit číslo čipu, nebo zaškrtnout půjčení čipu!';
  //   window.scrollTo({ top: 0, behavior: 'smooth' }); // Scrollování nahoru pro zobrazení chybové zprávy
  //   return; // Ukončení odesílání přihlášky
  // }

  // spuštění načítání požadavku
  isFormLoading.value = true;


  let entriesToUpdate = [];

  // Shromáždění ID z ubytování a jídla
  const raceServiceIds = [
    ...Object.values(selectedAccommodations.value).filter(Boolean), // ID ubytování
    ...Object.values(selectedMeals.value).flatMap(mealTypes => Object.values(mealTypes)).filter(Boolean) // ID jídla
  ];

  // Přidání si_rent do race_services, pokud je checkbox zaškrtnutý
  if (siRentChecked.value && siRentData.value) {
    raceServiceIds.push(siRentData.value.id);  // Přidat si_rent ID
  } else {
    // Pokud není zaškrtnutý, odeber si_rent ID z race_services, pokud tam je
    const index = raceServiceIds.indexOf(siRentData.value.id);
    if (index > -1) {
      raceServiceIds.splice(index, 1);
    }
  }


  // --- sběr other služeb ---
  Object.values(selectedOther.value)
  .filter(id => id)
  .forEach(id => raceServiceIds.push(id))
  
  
  // Přidání hlavního záznamu s aktualizací vybraných dat
  entriesToUpdate.push({
    ...currentSelectedEntry.mainEntry,
    competitor_category: selectedCategories.value[currentSelectedEntry.mainEntry.race], // Vybraná kategorie pro hlavní záznam
    id: currentSelectedEntry.mainEntry.id, // ID hlavního záznamu
    race: currentSelectedEntry.mainEntry.race, // ID závodu hlavního záznamu
    si_number: mainEntry.value.si_number === "" ? null : mainEntry.value.si_number,
    note: mainEntry.value.note,
  });

  // Přidání ostatních záznamů s aktualizací vybraných dat
  currentSelectedEntry.otherEntries.forEach(otherEntry => {
    entriesToUpdate.push({
      ...otherEntry,
      competitor_category: selectedCategories.value[otherEntry.race] === "" ? null : selectedCategories.value[otherEntry.race],
      id: otherEntry.id, // ID záznamu
      race: otherEntry.race, // ID závodu
      si_number: mainEntry.value.si_number === "" ? null : mainEntry.value.si_number, // Číslo čipu z hlavního záznamu (předpokládáme stejné pro všechny závody)
      start_preference: selectedStarts.value[otherEntry.race],
      note: mainEntry.value.note,

    });
  });

  // Kontrola pro závody, které nemají odpovídající entry, ale byla vybrána kategorie
  filteredRaces.value.forEach(race => {
    if (selectedCategories.value[race.id] && !currentSelectedEntry.otherEntries.some(entry => entry.race === race.id)) {
      entriesToUpdate.push({
        id: null,
        race: race.id,
        competitor_category: selectedCategories.value[race.id] === "" ? null : selectedCategories.value[race.id],
        si_number: mainEntry.value.si_number === "" ? null : mainEntry.value.si_number,
        start_preference: selectedStarts.value[race.id],
      });
    }
    
  });

  const entryEditQueryRole = currentSelectedEntry.mainEntry.role ?? 'null';

  let submissionData = {
    entries: entriesToUpdate,
    race_services: raceServiceIds,
  };


  try {
    await apiClient.put(`event/${eventId}/entry/edit/?entry_id=${entryEditQueryRole}`, submissionData);
    snackbar.success(t('entry.update_success'));
    errorMessage.value = '';
    isFormLoading.value = false; // Dočasně, protože jinak se to loaduje kvůli pomalému načítání položek níže a vypadá to divně (succes se zobrazí, ale pořád se to loaduije, kvůli načtení edit a create entry) 
    await fetchRaceServices(eventId);
    await fetchEntries(); // Znovu načtení dat o uživateli
    snapshotBaseline()
    // Aktualizace údajů uživatele na toho, který byl vybraný předtím
    if (allEntries.value.some(e => e.user === keepId)) {
      selectedEntryId.value = keepId;
      onEntrySelect(keepId);
    }

  } catch (error) {
    console.error("Chyba při odesílání přihlášky:", error);
    if (error.response) {
      if (error.response.status === 423) {
        errorMessage.value = error.response.data.error || 'Přihlášku se nepodařilo aktualizovat.';
      } else if (error.response.status === 409) {
        errorMessage.value = error.response.data.error;
      } else {
        errorMessage.value = t('entry.update_failed');
      }
    } else {
      errorMessage.value = t('entry.update_failed');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scrollování nahoru pro zobrazení chybové zprávy
  } finally {
    isFormLoading.value = false;  // Ukončení načítání po zpracování odpovědi
  }
}

async function deleteEntries() {
 // Najděte vybrané entry pro uživatele
 const selectedUserEntry = allEntries.value.find(entry => entry.user === selectedEntryId.value);
  
  if (!selectedUserEntry) {
    console.error("Entry pro vybraného uživatele nebylo nalezeno.");
    return;
  }

  // Získání ID všech přihlášek (včetně hlavního entry a ostatních entry) pro smazání
  const entryIdsToDelete = [selectedUserEntry.mainEntry.id, ...selectedUserEntry.otherEntries.map(entry => entry.id)];

  // Odeslání požadavku na smazání na server
  try {
    await apiClient.delete(`event/${eventId}/entry/edit/`, {
      data: { entry_id: entryIdsToDelete }
    });
    console.log('Všechny přihlášky pro vybraného uživatele byly úspěšně smazány.');
    deleteDialog.value = false;
    snackbar.success(t('entry.delete_success'));
    router.push(`/soutez/${eventId}`);
    // Zde můžete přidat logiku pro aktualizaci uživatelského rozhraní nebo přesměrování
  } catch (error) {
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scrollování nahoru pro zobrazení chybové zprávy
    errorMessage.value = t('entry.delete_failed');
    console.error("Chyba při mazání přihlášek:", error.response?.data?.error || error.message);
  }
}


///////////////////////////////////////////////////////////////
//
//          Vytvoření přihlášek
//
///////////////////////////////////////////////////////////////


const categoryMain = [v => !!v || t('entry.must_select_main_category')];
const validEmail = [value => !value || /^[\w-.]+@([\w-]+\.)+[\w-]{2,24}$/.test(value) ? true : t('rules.invalid_email')];
const requiredField = [(v) => !!v || t('rules.required')];


// Zobrazení select pro výběr uživatele
const userOptions = computed(() =>
  users.value.map(user => ({
    title: `${user.last_name} ${user.first_name}${user.competitor_index ? ` (${user.competitor_index})` : ''}`,
    value: user.id,
  }))
);

// Aktualizace kategorií podle věku a pohlaví vybraného uživatele
const updateCategoriesForUser = () => {
  if (!event.value?.races || event.value.races.length === 0 || !selectedUser.value) return;

  // Získání aktuálního věku a pohlaví uživatele
  const userAge = calculateUserAge(selectedUser.value.birth_year);
  const userSex = selectedUser.value.competitor_gender;
  const userClub = selectedUser.value.competitor_club;
  const eventOrganizerClub = event.value.club;

  // Ověříme, zda je uživatel ze stejného oddílu jako organizátor
  const isSameClub = isUserInSameClub(userClub, eventOrganizerClub);

  // Aktualizace kategorií pro race0 (hlavní závod)
  race0.value = {
    ...race0.value,
    categories: filterCategoriesByAgeAndSex(event.value.races[0].categories, userAge, userSex, isSameClub),
  };

  // Aktualizace kategorií pro všechny závody
  races.value = event.value.races.slice(1).map(race => ({
    ...race,
    categories: [{ id: null, category_name: t('entry.do_not_register') }, ...filterCategoriesByAgeAndSex(race.categories, userAge, userSex, isSameClub)],
  }));
};

// Sledování změn v ID vybraného uživatele a aktualizace kategorií
watch(selectedUserId, (newUserId) => {
  const user = users.value.find(u => u.id === newUserId);
  selectedUser.value = user || {}; // Aktualizace vybraného uživatele

  selectedCategoryRace0.value = null; // Reset hlavní kategorie
  Object.keys(selectedCategoriesForCreateEntry.value).forEach(key => {
    selectedCategoriesForCreateEntry.value[key] = undefined;
  });

  updateCategoriesForUser(); // Pouze aktualizace kategorií, bez volání API
});

// Sledování změn v roce narození a pohlaví -> RESETUJEME kategorie a přepočítáme možnosti
watch(
  [() => selectedUser.value.birth_year, () => selectedUser.value.competitor_gender],
  (newValues, oldValues) => {
    const [newBornYear, newSex] = newValues;
    const [oldBornYear, oldSex] = oldValues;

    if (newBornYear && newSex && (newBornYear !== oldBornYear || newSex !== oldSex)) {
      console.log('Rok narození nebo pohlaví bylo změněno, resetuji výběr kategorií.');

      // Reset hlavní kategorie a kategorií pro všechny závody
      selectedCategoryRace0.value = null;
      Object.keys(selectedCategoriesForCreateEntry.value).forEach(key => {
        selectedCategoriesForCreateEntry.value[key] = undefined;
      });

      // Aktualizace dostupných kategorií
      updateCategoriesForUser();
    }
  },
  { deep: true }
);

// Sledování změny hlavní kategorie a automatické nastavení stejné kategorie ve všech závodech
watch(selectedCategoryRace0, (newCategoryId) => {
  if (!newCategoryId || !race0.value) return;

  // Najdeme název kategorie podle jejího ID
  const selectedCategoryName = race0.value.categories.find(category => category.id === newCategoryId)?.category_name;

  if (selectedCategoryName) {
    // Pro všechny závody nastavíme stejnou kategorii (pokud existuje)
    races.value.forEach(race => {
      const matchingCategory = race.categories.find(category => category.category_name === selectedCategoryName);
      if (matchingCategory) {
        selectedCategoriesForCreateEntry.value[race.id] = matchingCategory.id;
      }
    });
  }
});

// Výpočet částky za mainCategory při výběru kategorie
const mainRaceFeeEventCreate = computed(() => {
  const race = race0.value
  if (!race) return 0
  const selId = selectedCategoryRace0.value
  if (!selId) return 0
  const fee = race.categories.find(c => c.id === selId)?.fee_event ?? 0
  return Number(fee) || 0
})

const isOrgSelectedInRace0Create = computed(() => {
  // nepřihlášení si ORG nikdy nevyberou → pravidlo se neuplatňuje
  if (!userStore.isLoggedIn) return false
  const race = race0.value
  if (!race) return false
  const selId = selectedCategoryRace0.value
  if (!selId) return false
  const cat = race.categories.find(c => c.id === selId)
  return cat?.option === 'ORG'
})

const fetchUsers = async (eventId) => {
  try {
    const response = await apiClient.get(`event/${eventId}/entry/create/`);
    // Pokud je odpověď pole, použijeme ji přímo, pokud ne, vytvoříme z odpovědi pole
    users.value = Array.isArray(response.data) ? response.data : [response.data];

    // Zkontrolujeme, zda máme alespoň jednoho uživatele, a pokud ano, nastavíme ho jako vybraného
    if (users.value.length > 0) {
      selectedUserId.value = users.value[0].id;
      selectedUser.value = users.value[0];
    }
    isLoadingCreate.value = false;
  } catch (error) {
    console.error('Error fetching users:', error);
  }
};

const fetchClubs = async () => {
  try {
    const response = await apiClient.get('club/');
    const all = Array.isArray(response.data) ? response.data : [];
    // pro jednorázové přihlášky nechceme nabízet REPRE/SCM
    clubs.value = all.filter(c => c?.club_type !== 'REPRE' && c?.club_type !== 'SCM');
  } catch (error) {
    console.error('Error fetching clubs:', error);
    clubs.value = [];
  }
};


const fetchEvent = async (eventId) => {
  try {
    const response = await apiClient.get(`event/?event_id=${eventId}`);
    
    if (!response.data) {
      throw new Error('Neplatná odpověď z API');
    }

    const eventData = response.data;
    event.value = eventData;

    // Pokud event obsahuje závody, zpracujeme je
    if (eventData.races && eventData.races.length > 0) {
      // Seřadíme závody podle race_date
      const sortedRaces = eventData.races.sort((a, b) => new Date(a.race_date) - new Date(b.race_date));

      // První závod s nejnižším ID
      const firstRace = sortedRaces[0];

      // Získání věku a pohlaví uživatele
      const userAge = calculateUserAge(selectedUser.value.birth_year);
      const userSex = selectedUser.value.competitor_gender;
      const userClub = selectedUser.value?.club;  // Oddíl uživatele
      const eventOrganizerClub = eventData.club;  // Oddíl organizátora

      // Ověříme, zda je uživatel ze stejného oddílu jako organizátor
      const isSameClub = isUserInSameClub(userClub, eventOrganizerClub);

      // Nastavení prvního závodu
      race0.value = {
        ...firstRace,
        categories: filterCategoriesByAgeAndSex(firstRace.categories, userAge, userSex, isSameClub),
      };

      // Seřazení zbývajících závodů podle data a přidání kategorie "Nepřihlásit"
      races.value = sortedRaces.slice(1).map(race => ({
        ...race,
        categories: [{ id: null, category_name: t('entry.do_not_register') }, ...filterCategoriesByAgeAndSex(race.categories, userAge, userSex, isSameClub)],
      }));

      // Zpracování závodů pro výběr kategorií
      allRaces.value = eventData.races.map(race => ({
        ...race,
        categories: [
          { id: null, category_name: "", option: "", fee: 0 },
          ...race.categories.sort((a, b) => a.category_name.localeCompare(b.category_name)),
        ],
      }));

      // Nastavení vybraných kategorií na základě přihlášek
      allRaces.value.forEach(race => {
        const foundEntry = entries.value.find(entry => entry.race === race.id);
        if (foundEntry) {
          selectedCategories.value[race.id] = foundEntry.competitor_category;
        }
      });

      // Uložení termínu přihlášek z prvního závodu
      if (allRaces.value.length > 0) {
        mainEntry.value = { termin_prihl: allRaces.value[0].entry_deadline_1 };
      }
    }

    isLoading.value = false;
  } catch (error) {
    console.error('Chyba při načítání dat o eventu, závodech nebo kategoriích:', error);
  }
};


// Získání URL na Propozice, pokud existují
const BaseUrl = ref(process.env.VUE_APP_API_BASE_URL.replace(/\/+$/, '').replace(/\/api\/?$/, '')); // Odstraní "api/" a nadbytečná lomítka
const bulletinFileLocation = computed(() => {
  if (!race0.value?.files) return null;

  const bulletinFile = race0.value.files.find(file => file.specific === "bulletin");
  return bulletinFile ? `${BaseUrl.value}${bulletinFile.location}` : null;
});

// Kontrola vyplněných údajů pro přihlášeného a nepřihlášeného uživatele
const isFormValid = computed(() => {
  if (!userStore.isLoggedIn) {
    // Zkontrolujte, že všechna pole jsou vyplněna
    return selectedUser.value.first_name &&
           selectedUser.value.last_name &&
           selectedUser.value.birth_year &&
           selectedUser.value.competitor_gender &&
           selectedCategoryRace0.value &&
           validEmailCheck(selectedUser.value.email) &&
           rules.siNumber(selectedUser.value.si_number) === true;
  }
  
  return selectedCategoryRace0.value && rules.siNumber(selectedUser.value.si_number) === true; // přihlášený zkontroluje jen výběr hlavní kategorie a číslo čipu
});

function validEmailCheck(email) {
  return !email || /^[\w-.]+@([\w-]+\.)+[\w-]{2,24}$/.test(email);
}

const siRentData = ref(null);

async function fetchRaceServices(eventId) {
  try {
    const response = await apiClient.get(`race_service_entry/?event_id=${eventId}`);
    accommodationsByDate.value = response.data.accommodations;
    Object.keys(accommodationsByDate.value).forEach(date => {
      accommodationsByDate.value[date].forEach(accommodation => {
        const limit = accommodation.limit !== null ? accommodation.limit : Infinity;
        const count = accommodation.count !== null ? accommodation.count : 0;
        accommodation.freeSpaces = limit !== Infinity ? limit - count : null;
      });
      selectedAccommodations.value[date] = '';
      createSelectedAccommodations.value[date] = '';
    });
    mealsByDate.value = response.data.foods;
    Object.keys(mealsByDate.value).forEach(date => {
      selectedMeals.value[date] = {}
      if (!createSelectedMeals.value[date]) createSelectedMeals.value[date] = {}
      mealsByDate.value[date].forEach(meal => {
        const code = meal.type // 'sn' | 'ob' | 've'
        selectedMeals.value[date][code] = null
        createSelectedMeals.value[date][code] = null
      })
    })
    
    snapshotBaseline()

    // Kontrola a nastavení siRentData
    if (response.data.si_rent && response.data.si_rent.length > 0) {
      siRentData.value = response.data.si_rent[0]; // Uložení prvního záznamu si_rent
    }

    // Nastavení other služeb – zde se načte pole other
    otherServices.value = response.data.other || [];
    // reset výběru v other services
    createSelectedOther.value = {}                 // zahoď starý objekt
    for (const key of Object.keys(groupedOtherServices.value)) {
      createSelectedOther.value[key] = null        // všechny skupiny na null
    }

  } catch (error) {
    console.error('Failed to fetch race services:', error);
  }
}

// pořadí typů jídla (kódy z backendu)
const FOOD_ORDER = ['sn', 'ob', 've']
// labely do UI (překlady)
const FOOD_LABELS = computed(() => ({
  sn: t('entry.breakfast'), // přidej do i18n
  ob: t('entry.lunch'),
  ve: t('entry.dinner'),
}))
const foodLabel = (code) => FOOD_LABELS.value[code] || code

// Výpočet zbývajících míst ubytování
const uniqueAccommodations = computed(() => {
  const accommodationMap = new Map();
  Object.values(accommodationsByDate.value).forEach(items => {
    items.forEach(item => {
      if (item.limit !== null && !accommodationMap.has(item.name)) {
        accommodationMap.set(item.name, item);
      }
    });
  });
  return Array.from(accommodationMap.values());
});



// other entries
// Seskupíme služby podle 'key'. Pokud služba nemá key, použijeme 'bez_klíče'

// Seskupení služeb podle jejich klíče ("key") – pokud služba nemá klíč, použije se "bez_klíče"
const groupedOtherServices = computed(() => {
  const groups = {};
  otherServices.value.forEach(service => {
    const key = service.key || 'bez_klice';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(service);
  });
  // Seřadíme varianty uvnitř každé skupiny podle hodnoty varianty
  Object.keys(groups).forEach(key => {
    groups[key].sort((a, b) => Number(a.variant) - Number(b.variant));
  });
  return groups;
});

// Objekt pro uložení vybrané varianty pro každou skupinu – klíč = hodnota atributu "key", hodnota = id vybrané varianty
// Pokud ve skupině je pouze jedna varianta, nastav ji automaticky
watch(groupedOtherServices, (newGroups) => {
  Object.keys(newGroups).forEach(key => {
    if (!(key in selectedOther.value)) {
      // Nastavíme defaultní hodnotu na null – checkbox tedy nebude zaškrtnutý.
      selectedOther.value[key] = null;
    }
  });
}, { immediate: true });


// spočítáme pro každou skupinu key, jaký má právě count a limit

const other_create_limits = computed(() => {
  const map = {}
  for (const [key, group] of Object.entries(groupedOtherServices.value)) {
    const sel = selectedOther.value[key] || group[0]?.id
    const svc = group.find(item => item.id === sel)
    if (svc && svc.limit != null) {
      const count = svc.count
      const limit = svc.limit
      map[key] = { count, limit, remaining: limit - count }
    }
  }
  return map
})


const other_edit_limits = computed(() => {
  const map = {}
  for (const [key, variants] of Object.entries(groupedOtherServices.value)) {
    // nejdřív bereme vybranou variantu, nebo první
    const selId = selectedOther.value[key] || variants[0]?.id
    const svc   = variants.find(v => v.id === selId)
    if (svc?.limit != null) {
      const count     = svc.count
      const limit     = svc.limit
      const remaining = limit - count
      map[key] = { count, limit, remaining }
    }
  }
  return map
})

const submitEntries = async () => {
  let raceEntries = [];
  let serviceEntries = [];

  // Příprava základních údajů o uživateli
  const prepareEntry = (entry) => {
    if (entry.si_number === "") {
      entry.si_number = null;
    }
    return entry;
  };

  // Kontrola si_number a si_rent
  // if (!event.value.disable_si_cards && !selectedUser.value.si_number && !selectedUser.value.si_rent) {
  //   errorMessage.value = 'Musíš vyplnit Číslo čipu nebo zaškrtnout Půjčení čipu.';
  //   window.scrollTo({ top: 0, behavior: 'smooth' }); // Scrollování nahoru pro zobrazení chybové zprávy
  //   return;
  // }

  // Přidání závodů
  if (selectedCategoryRace0.value) {
    raceEntries.push(prepareEntry({
      race: race0.value.id,
      competitor_category: selectedCategoryRace0.value,
      ...selectedUser.value
    }));
  }

  races.value.forEach(race => {
    if (selectedCategoriesForCreateEntry.value[race.id]) {
      raceEntries.push(prepareEntry({
        race: race.id,
        competitor_category: selectedCategoriesForCreateEntry.value[race.id],
        start_preference: selectedStartsEntry.value[race.id],
        ...selectedUser.value
      }));
    }
  });

  // Přidání výběru ubytování a jídla
  Object.keys(createSelectedAccommodations.value).forEach(date => {
    if (createSelectedAccommodations.value[date]) serviceEntries.push(createSelectedAccommodations.value[date])
  })

  Object.keys(createSelectedMeals.value).forEach(date => {
    Object.keys(createSelectedMeals.value[date]).forEach(mealType => {
    const id = createSelectedMeals.value[date][mealType]
    if (id) serviceEntries.push(id)
   })
  })

  // Pokud je zaškrtnuto půjčení čipu a existuje ID pro si_rent, přidej ho do race_services
  if (selectedUser.value.si_rent && siRentData.value) {
    serviceEntries.push(siRentData.value.id); // Přidání si_rent ID k race_services
  }

  // Přidání vybraných Other služeb z grouped výběru (selectedOther)
  Object.keys(createSelectedOther.value).forEach(key => {
    if (createSelectedOther.value[key]) serviceEntries.push(createSelectedOther.value[key])
  })

  // Kontrola a odeslání přihlášek
  if (!selectedCategoryRace0.value) {
    alert(t('entry.must_select_main_category'));
    return;  // Zastavení funkce, pokud není vybraná kategorie pro race0
  }

  // spuštění načítání požadavku
  isFormLoading.value = true;


  if (raceEntries.length > 0) {
    raceEntries[0].race_services = serviceEntries; // raceservices se přidají do prvního záznamu entry
    try {
      await apiClient.post(`event/${eventId}/entry/create/`, { races: raceEntries });

      // Pokud není uživatel přihlášený, přesměruj ho po úspěšném odeslání na stránku soutěže
      if (!userStore.isLoggedIn) {
        snackbar.success(t('entry.create_success'));
        router.push(`/soutez/${eventId}`);
        return; // Ukončení funkce, aby se nespouštěly další části kódu
      }

      snackbar.success(t('entry.create_success'));
      errorMessage.value = '';
      
      await fetchEntries();
      await fetchUsers((eventId));
      await fetchRaceServices(eventId);
      snapshotBaseline()


      // Aktualizace údajů uživatele na toho prvního v seznamu
      if (allEntries.value.length > 0) {
        selectedEntryId.value = allEntries.value[0].user;
        onEntrySelect(selectedEntryId.value); // Aktualizace formuláře s nově vybraným uživatelem
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('Došlo k chybě při odesílání přihlášek:', error);
      window.scrollTo({ top: 0, behavior: 'smooth' }); // Scrollování nahoru pro zobrazení chybové zprávy
      if (error.response) {
        if (error.response.status === 423) {
          errorMessage.value = error.response.data.error || t('entry.limit_accommodation_exhausted');
        } else if (error.response.status === 409) {
          errorMessage.value = error.response.data.error;
        } else {
          errorMessage.value = t('entry.create_failed');
        }
      } else {
        errorMessage.value = t('entry.create_failed');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' }); // Scrollování nahoru pro zobrazení chybové zprávy
    } finally {
      isFormLoading.value = false;  // Ukončení načítání po zpracování odpovědi
    }
  }
};


// CENY   

// Přidání výpočtu celkové sumy pro editaci přihlášky
const totalSumEdit = computed(() => {
  let sum = 0;

  // Funkce pro získání správné ceny podle termin_prihl
  const getCategoryFee = (category, race) => {
    // Hlavní závod (first race) vždy používá fee_event
    if (race.id === mainRace.value.id) {
      return category.fee_event || 0; // Pokud není fee_event dostupné, vrať 0
    }
    // Ostatní závody: používá increased_fee, pokud je termin_prihl "2", jinak fee
    if (mainEntry.value && mainEntry.value.termin_prihl === "2" && category.increased_fee) {
      return category.increased_fee;
    }
    return category.fee || 0; // Pokud není fee dostupné, vrať 0
  };

  // ORG v race0 => zdarma jen ubytování + strava (kategorie + other + si-rent necháváme)
  const zeroAccFood = isOrgSelectedInRace0Edit.value;

  // Suma za ubytování
  Object.values(selectedAccommodations.value).forEach(id => {
    if (id) {
      const date = Object.keys(accommodationsByDate.value).find(date =>
        accommodationsByDate.value[date].some(item => item.id === id)
      );
      const accommodation = accommodationsByDate.value[date].find(item => item.id === id);
      if (!zeroAccFood) sum += accommodation.price;
    }
  });

  // Suma za jídlo
  Object.values(selectedMeals.value).forEach(dayMeals => {
    Object.values(dayMeals).forEach(mealId => {
      if (mealId) {
        const date = Object.keys(mealsByDate.value).find(date =>
          mealsByDate.value[date].some(meal => meal.id === mealId)
        );
        const meal = mealsByDate.value[date].find(meal => meal.id === mealId);
        if (!zeroAccFood) sum += meal.price;
      }
    });
  });

  // Suma za hlavní závod (vždy započítán)
  if (selectedCategories.value[mainRace.value.id]) {
    const category = mainRace.value.categories.find(cat => cat.id === selectedCategories.value[mainRace.value.id]);
    if (category) {
      sum += getCategoryFee(category, mainRace.value);
    }
  }

  // Suma za kategorie ostatních závodů
  Object.keys(selectedCategories.value).forEach(raceId => {
    if (raceId != mainRace.value.id && selectedCategories.value[raceId]) {
      const race = allRaces.value.find(r => r.id === parseInt(raceId));
      if (race) {
        const category = race.categories.find(cat => cat.id === selectedCategories.value[raceId]);
        if (category) {
          sum += getCategoryFee(category, race);
        }
      }
    }
  });

  // Přidání ceny za si_rent, pokud je checkbox zaškrtnutý
  if (siRentChecked.value && siRentData.value) {
    sum += siRentData.value.price; // si-rent se neslevňuje
  }

   // Přidání ceny za ostatní služby (other)
   Object.values(selectedOther.value).forEach(id => {
    if (id) {
      const svc = otherServices.value.find(s => s.id === id);
      if (svc && typeof svc.price === 'number') {
        sum += svc.price;
      }
    }
  });

  return sum;
});

// Přidání výpočtu celkové sumy pro vytváření přihlášky
const totalSumCreate = computed(() => {
  let sum = 0;

  // Funkce pro získání správné ceny podle entry_deadline z prvního závodu (race0)
  const getCategoryFee = (category, race) => {
    // Hlavní závod (first race) vždy používá fee_event
    if (race.id === race0.value.id) {
      return category.fee_event || 0; // Pokud není fee_event dostupné, vrať 0
    }

    // Ostatní závody: používá increased_fee, pokud je termín po deadline1, jinak fee
    const now = new Date();
    const deadline1 = new Date(race0.value.entry_deadline_1);
    const deadline2 = new Date(race0.value.entry_deadline_2);

    if (now > deadline1 && (!deadline2 || now < deadline2) && category.increased_fee) {
      return category.increased_fee;
    }
    return category.fee || 0; // Pokud není fee dostupné, vrať 0
  };

  // ORG v race0 (jen přihlášený) => zdarma jen ubytování + strava
  const zeroAccFood = isOrgSelectedInRace0Create.value;

  // Suma za ubytování
  Object.values(createSelectedAccommodations.value).forEach(id => {
    if (id) {
      const date = Object.keys(accommodationsByDate.value).find(date =>
        accommodationsByDate.value[date].some(item => item.id === id)
      );
      const accommodation = accommodationsByDate.value[date].find(item => item.id === id);
      if (!zeroAccFood) sum += accommodation.price;
    }
  });

  // Suma za jídlo
  Object.values(createSelectedMeals.value).forEach(dayMeals => {
    Object.values(dayMeals).forEach(mealId => {
      if (mealId) {
        const date = Object.keys(mealsByDate.value).find(date =>
          mealsByDate.value[date].some(meal => meal.id === mealId)
        );
        const meal = mealsByDate.value[date].find(meal => meal.id === mealId);
        if (!zeroAccFood) sum += meal.price;
      }
    });
  });

   // Přidání ceny za půjčení čipu, pokud je vybráno a existuje si_rent
   if (selectedUser.value.si_rent && siRentData.value) {
    sum += siRentData.value.price;
  }

  // Suma za hlavní závod (vždy započítán)
  if (selectedCategoryRace0.value) {
    const category = race0.value.categories.find(cat => cat.id === selectedCategoryRace0.value);
    if (category) {
      sum += getCategoryFee(category, race0.value);
    }
  }

  // Suma za kategorie ostatních závodů
  Object.keys(selectedCategoriesForCreateEntry.value).forEach(raceId => {
    if (raceId != race0.value.id && selectedCategoriesForCreateEntry.value[raceId]) {
      const race = races.value.find(r => r.id === parseInt(raceId));
      if (race) {
        const category = race.categories.find(cat => cat.id === selectedCategoriesForCreateEntry.value[raceId]);
        if (category) {
          sum += getCategoryFee(category, race);
        }
      }
    }
  });

  // Suma za ostatní služby (other services)
  Object.keys(createSelectedOther.value).forEach(key => {
    const variantId = createSelectedOther.value[key];
    if (variantId) {
      // Získáme skupinu Other služeb podle key
      const group = groupedOtherServices.value[key];
      if (group) {
        // Vybereme tu službu, která odpovídá id
        const service = group.find(s => s.id === variantId);
        if (service && service.price !== null) {
          sum += Number(service.price);
        }
      }
    }
  });
  

  return sum;
});

// click pro ubytování v create (jednotlivci)
const isSelectClickedCreate = ref(false)
function onSelectClickCreate(date) { isSelectClickedCreate.value = true }

watch(
  () => JSON.parse(JSON.stringify(createSelectedAccommodations.value)),
  (newVal, oldVal) => {
    if (!isSelectClickedCreate.value) return
    let changedDate = null
    Object.keys(newVal).forEach(date => {
      const newId = newVal[date]
      const oldId = oldVal ? oldVal[date] : null
      if (newId !== oldId) {
        changedDate = date
        const selected = accommodationsByDate.value[date].find(a => a.id === newId)
        if (!selected) return
        const variant = selected.variant
        Object.keys(accommodationsByDate.value).forEach(otherDate => {
          if (otherDate === changedDate) return
          if (accommodationsByDate.value[otherDate].length <= 1) return
          const same = accommodationsByDate.value[otherDate].find(a => a.variant === variant)
          if (same) createSelectedAccommodations.value[otherDate] = same.id
        })
      }
    })
    isSelectClickedCreate.value = false
  },
  { deep: true }
)


// click pro ubytování (jednotlivci) - v edit i hromadné create. Nevím, jestlity hromadný nemají být jako to nadtím

let isSelectClicked = ref(false); // Příznak pro kontrolu kliknutí

// Funkce pro zachycení kliknutí na select
function onSelectClick(date) {
  console.log(`Došlo ke kliknutí na select pro datum ${date}`);
  isSelectClicked.value = true; // Aktivuje watcher při kliknutí
}

// Watcher sleduje změny a aplikuje je jen tehdy, když je select kliknutý
watch(() => JSON.parse(JSON.stringify(selectedAccommodations.value)), (newVal, oldVal) => {
  if (!isSelectClicked.value) {
    console.log("Změny ignorovány, nebylo kliknuto na select.");
    return;
  }

  console.log("Aktuální selectedAccommodations:", newVal);
  let changedDate = null;

  // Projdeme všechny datumy a zjistíme, kde došlo ke změně varianty
  Object.keys(newVal).forEach(date => {
    const newAccommodationId = newVal[date];
    const oldAccommodationId = oldVal ? oldVal[date] : null;

    // Pokud se hodnota změnila, zjistíme variantu
    if (newAccommodationId !== oldAccommodationId) {
      changedDate = date;
      console.log(`Změněná hodnota pro datum: ${date}, Nové ID: ${newAccommodationId}, Staré ID: ${oldAccommodationId}`);
      
      const selectedAccommodation = accommodationsByDate.value[date].find(acc => acc.id === newAccommodationId);
      if (selectedAccommodation) {
        const selectedVariant = selectedAccommodation.variant;
        console.log(`Vybraná varianta pro datum ${date}: ${selectedVariant}`);

        // Projdeme ostatní datumy a nastavíme stejnou variantu, pokud existuje
        Object.keys(accommodationsByDate.value).forEach((otherDate) => {
          // Neprovádíme změnu na poli, kde došlo ke změně, ani tam, kde je pouze jedna možnost
          if (otherDate !== changedDate && accommodationsByDate.value[otherDate].length > 1) {
            const accommodationWithSameVariant = accommodationsByDate.value[otherDate].find(
              (acc) => acc.variant === selectedVariant
            );

            // Pokud existuje ubytování se stejnou variantou, nastavíme ho
            if (accommodationWithSameVariant) {
              console.log(`Nastavuji variantu ${selectedVariant} pro datum ${otherDate}`);
              selectedAccommodations.value[otherDate] = accommodationWithSameVariant.id;
            } else {
              console.log(`Nenalezeno ubytování s variantou ${selectedVariant} pro datum ${otherDate}`);
            }
          } else if (accommodationsByDate.value[otherDate].length === 1) {
            console.log(`Datum ${otherDate} má pouze jednu možnost, změny se neprovádějí.`);
          }
        });
      } else {
        console.log(`Ubytování s ID ${newAccommodationId} nebylo nalezeno pro datum ${changedDate}`);
      }
    }
  });

  isSelectClicked.value = false; // Resetuje příznak po dokončení změn
  }, { deep: true });


// předvýběr první kategorie ze seznamu kategorií
// Předvýběr první kategorie pro hlavní závod
watch(
  () => race0.value.categories,
  (cats) => {
    if (cats?.length) {
      selectedCategoryRace0.value = cats[0].id
    }
  },
  { immediate: true }
)

// Předvýběr první kategorie pro každý další závod
watch(
  () => races.value,
  (all) => {
    all.forEach(race => {
      const cats = race.categories
      if (cats?.length && !selectedCategoriesForCreateEntry.value[race.id]) {
        selectedCategoriesForCreateEntry.value[race.id] = cats[0].id
      }
    })
  },
  { immediate: true, deep: true }
)



///////////////////////////////////////////////////////////////
//
//          Hromadne vytvoreni prihlasek (EntryPageBulkDialog)
//
///////////////////////////////////////////////////////////////

const dialogBulkEntry = ref(false);

async function openBulkDialog() {
  if (!users.value.length) {
    await fetchUsers(eventId);
  }
  dialogBulkEntry.value = true;
}

async function onBulkEntrySuccess() {
  await fetchRaceServices(eventId);
  await fetchEntries();
  await fetchUsers(eventId);
}


// Hlídání změny v polích a případné zobrazení alertu při odchodu s neuloženými změnami
const baselineJSON = ref(null)

function currentState() {
  return {
    // EDIT formulář
    selectedCategories: selectedCategories.value,
    selectedAccommodations: selectedAccommodations.value,
    selectedMeals: selectedMeals.value,
    selectedOther: selectedOther.value,
    siRentChecked: siRentChecked.value,
    mainSi: mainEntry.value?.si_number ?? null,
    mainNote: mainEntry.value?.note ?? '',
    selectedStarts: selectedStarts.value,

    // CREATE formulář (pokud chceš hlídat i ten)
    createSelectedAccommodations: createSelectedAccommodations.value,
    createSelectedMeals: createSelectedMeals.value,
    createSelectedOther: createSelectedOther.value,
    selectedUser: selectedUser.value,
    selectedCategoryRace0: selectedCategoryRace0.value,
    selectedCategoriesForCreateEntry: selectedCategoriesForCreateEntry.value,
    selectedStartsEntry: selectedStartsEntry.value,
  }
}

function snapshotBaseline() {
  baselineJSON.value = JSON.stringify(currentState())
}

const isDirty = computed(() => {
  if (!baselineJSON.value) return false
  return baselineJSON.value !== JSON.stringify(currentState())
})


// Alert při zavírání okna pro neuložené změny
const confirmLeaveDialog = ref(false)
const pendingRoute = ref(null)
const allowProgrammaticNav = ref(false) // ochrana proti smyčce

function askBeforeLeave(to) {
  // Jednorázová přihláška (nepřihlášený) -> nic nekontrolovat
  if (!userStore.isLoggedIn) return true
  
  if (allowProgrammaticNav.value) return true       // např. po potvrzení
  if (!isDirty.value)          return true          // nic se nezměnilo → pustit
  pendingRoute.value = to
  confirmLeaveDialog.value = true                   // ukaž dialog
  return false                                      // a navigaci nyní stopni
}

onBeforeRouteLeave((to, from) => askBeforeLeave(to))
onBeforeRouteUpdate((to, from) => askBeforeLeave(to))

async function confirmLeave() {
  // uživatel chce pryč
  confirmLeaveDialog.value = false
  const target = pendingRoute.value
  pendingRoute.value = null
  allowProgrammaticNav.value = true
  try {
    if (target) {
      await router.push(target)
    }
  } finally {
    allowProgrammaticNav.value = false
  }
}

function stayHere() {
  // zůstat na stránce
  confirmLeaveDialog.value = false
  pendingRoute.value = null
}


</script>


<style scoped>


.text-red {
  color: red;
}

.v-col-12 {
  padding-left: 5px !important;
  padding-right: 5px !important;
}

@media (max-width: 600px) {
  .responsive-btn {
    width: 100%;
    margin-bottom: 10px;
  }
  .responsive-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .price {
    width: 100%;
    text-align: center;
    margin-bottom: 10px;
    color: red;
    display: block;
    font-weight: bold;
  }
}
@media (min-width: 601px) {
  .responsive-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .price {
    color: red;
    margin-left: 10px;
    margin-right: auto;
    font-weight: bold;

  }
}


/*********** Část CSS pro dialog hromadných přihlášek ****************/
/* Obal pro horizontální scrollování */
.scroll-container {
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-gutter: stable;
  height: 75vh;
  border: none;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;

}

@media (max-width: 600px) {
  .scroll-container {
    height: 65vh !important;
  }
}

/* Obsah pro roztažení */
.scroll-content {
  display: flex;
  flex-direction: column;
  min-width: max-content;
}

/* Řádek uživatele — barvy z Vuetify theme (light/dark) */
.user-row {
  display: flex;
  align-items: center;
  min-width: 1000px; /* Změň podle potřeb */
  padding: 3px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
}

.bulk-row-selected {
  background-color: rgb(var(--v-theme-upload-ok)) !important;
}

.bulk-row-unselected {
  background-color: rgb(var(--v-theme-surface)) !important;
}

.bulk-checkbox-selected {
  background-color: rgb(var(--v-theme-upload-ok)) !important;
  border-radius: 4px;
}

.bulk-checkbox-unselected {
  background-color: rgb(var(--v-theme-upload-create)) !important;
  border-radius: 4px;
}


/* Buňky s obsahem */
.user-cell {
  flex: 1;
  min-width: 200px; /* Minimální šířka sloupce */
  padding: 4px 6px 4px 6px;
  display: flex;
  align-items: stretch;
}


.race-select-container {
  display: flex;
  flex-direction: column; /* Položí oba selecty pod sebe */
  width:200px;
  gap: 3px; /* Přidá mezery mezi selecty */
}




</style>